<template lang="pug">
li.content
    .drag-handle
        svg(xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor")
            circle(cx="5.5" cy="3" r="1.25")
            circle(cx="10.5" cy="3" r="1.25")
            circle(cx="5.5" cy="8" r="1.25")
            circle(cx="10.5" cy="8" r="1.25")
            circle(cx="5.5" cy="13" r="1.25")
            circle(cx="10.5" cy="13" r="1.25")
    .thumbnail(
        v-if="filetype === 'image' && thumbnail"
        :style="{ 'min-width': `${width}px`, height: `${height}px` }"
    )
        img(:src="thumbnail" :style="{ width: `${width}px`, height: `${height}px`, objectFit: 'cover' }")
    a.icon.image(
        v-else-if="filetype === 'image'"
        href="#" @click.stop.prevent="$emit('crop', media)"
        :style="{ 'min-width': `${width}px`, height: `${height}px` }"
    )
        i.el-icon-fa-file-image.fas

    a.icon.file(
        v-else-if="filetype === 'file'"
        href="#" @click.stop.prevent="$emit('edit', media)"
        :style="{ 'min-width': `${width}px`, height: `${height}px` }"
    )
        i.el-icon-fa-file-alt.fas

    a.icon(
        v-else
        :class="filetype"
        href="#" @click.stop.prevent="$emit('edit', media)"
        :style="{ 'min-width': `${width}px`, height: `${height}px` }"
    )
        i.fas(:class="`el-icon-fa-file-${filetype}`")

    .filename(v-line-clamp:24="1") {{ filename }}
    .filesize {{ filesize | prettyBytes }}
    .actions
        a(:href="`${src}&download`") {{ $t('download') }}
        //- TODO: add modal like for images
        //- a(href="#" @click.stop.prevent="$emit('edit', media)") {{ $t('edit') }}
        //- a(href="#") {{ $t('crop') }}
    slot
</template>

<i18n>
{
    "de": {
        "download": "herunterladen",
        "Drag to reorder": "Reihenfolge per Drag & Drop ändern"
    }
}
</i18n>

<script>
import _ from 'lodash'
import mime from 'mime'

import AuthApi from '@/store/api/system/auth';

export default {
    name: 'MediaPreview',
    props: {
        media: {
            type: Object,
            required: true
        },
        width: {
            type: Number,
            required: true
        },
        height: {
            type: Number,
            required: true
        },
        sortable: {
            type: Boolean,
            default: false
        },
    },
    computed: {
        type() {
            return this.media.type
        },
        file() {
            switch (this.type) {
                case 'file':
                    return _.get(this.media, 'file')
                case 'image':
                case 'video':
                    return _.get(this.media, `${this.type}.source`)
            }
        },
        filename() {
            return _.get(this.file, 'preferred_name') || _.get(this.file, 'name')
        },
        filesize() {
            return parseInt(_.get(this.file, 'size'))
        },
        filetype() {
            let filetype

            const m = _.get(this.file, 'mime')
            const suffix = mime.getExtension(m)
            switch (suffix) {
                case 'jpg':
                case 'jpeg':
                case 'png':
                    filetype = 'image'
                    break;

                case 'mp4':
                case 'mpeg':
                case 'webm':
                case 'qt':
                    filetype = 'video'
                    break;

                case 'zip':
                case 'rar':
                case 'gzip':
                case 'tar':
                case 'tar.gz':
                case 'tar.gzip':
                    filetype = 'archive'
                    break;

                case 'doc':
                case 'docx':
                case 'docm':
                case 'docb':
                case 'dotx':
                case 'dotm':
                    filetype = 'word'
                    break;

                case 'ppt':
                case 'pptx':
                case 'pptm':
                    filetype = 'powerpoint'
                    break;

                case 'xls':
                case 'xlsx':
                    filetype = 'excel'
                    break;

                case 'csv':
                    filetype = 'csv'
                    break;

                case 'mp3':
                case 'wav':
                    filetype = 'audio'
                    break;

                default:
                    filetype = 'file'
            }

            return filetype
        },
        src() {
            const storageRoute = _.trim(`${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PRIVATE_STORAGE_ROUTE}`, '/')

            let table = 'uploads'

            let id = this.file.id
            const name = this.file.name

            switch (this.type) {
                case 'file':
                    if (! this.file.upload) table = 'files'
                    break;

                case 'image':
                    if (! this.file.upload) {
                        table = 'images'
                        id = _.get(this.media, `${this.type}.id`)
                    }
                    break;

                case 'video':
                    if (! this.file.upload) {
                        table = 'videos'
                        id = _.get(this.media, `${this.type}.id`)
                    }
                    break;
            }

            const token = AuthApi.getToken()
            return `${storageRoute}/${table}/${id}/${name}?token=${token}`
        },
        thumbnail() {
            if (this.type === 'image') {
                const fit = `fit=crop&crop=focalpoint&x=.5&y=.5&z=1&w=${this.width}&h=${this.height}`
                // const fit = `fit&h=${this.height}`
                return `${this.src}&${fit}`
            }
        },
    }
}
</script>

<style lang="sass" scoped>
li.content
    background: rgba(255,255,255,.4)
    margin-bottom: .75rem
    border-radius: 4px
    display: grid
    grid-template-rows: 24px 18px
    grid-template-columns: max-content auto
    column-gap: .5rem
    padding-right: .5rem

    .drag-handle
        display: none

    &.deleted
        .thumbnail, .filename, .filesize, .actions, .icon
            opacity: .6
            filter: grayscale(1) blur(1px)
            user-select: none
            pointer-events: none

    .thumbnail
        position: relative
        overflow: hidden
        border-radius: 4px
        img
            display: block
            object-fit: cover
        .el-button.crop-image
            position: absolute
            top: calc(50% - 19px)
            right: calc(50% - 19px)
            background: rgba(100,100,100,.8)
            border: none
            width: 38px
            height: 38px
            pointer-events: none
            opacity: 0
            transition: opacity .3s ease
            ::v-deep i
                font-size: 1.2rem

    .icon
        font-size: 1.7rem
        display: flex
        justify-content: center
        align-items: center
        background-color: $color-body
        border-radius: 4px
        color: rgba(0, 0, 0, 0.7)

    .filename, .filesize, .actions
        line-height: 1.3
        grid-column: 2

    .filename
        font-size: 1rem
        line-height: 24px
        padding-top: 2px

    .filesize
        font-size: .8rem
        padding-top: 2px

    .actions
        display: none
        a
            text-decoration: none
            &:hover
                text-decoration: underline

    &.edit
        grid-template-rows: 24px 18px 22px
        .filename
            padding-right: 30px
            font-size: 1.2rem
        .thumbnail:hover
            .el-button.crop-image
                opacity: 1
        .icon
            font-size: 3rem
        .actions
            display: initial

    &.edit.is-sortable
        grid-template-columns: 24px max-content auto
        .drag-handle
            display: flex
            align-items: center
            justify-content: center
            grid-row: 1 / -1
            grid-column: 1
            cursor: grab
            color: #c0c4cc
            border-right: 1px solid #ebeef5
            border-radius: 4px 0 0 4px
            transition: color 0.15s ease, background 0.15s ease
            &:hover
                color: $--color-primary
                background: mix(white, $--color-primary, 92%)
            &:active
                cursor: grabbing
        .icon, .thumbnail
            grid-column: 2
        .filename, .filesize, .actions
            grid-column: 3

</style>
